/**
 * rig_normals — smooth the SPLIT normals of a Meshy rig across its UV seams, topology untouched
 * (shader gaps program, coordinator, 2026-09-30).
 *
 * THE MESHY ATLAS LAW (atlas/CLAUDE.md): a Meshy mesh's atlas is fragmented to the triangle, and every UV seam is a
 * split vertex — ~1.6 vertices per position on the served rigs. Meshy writes each chart its OWN normals, so the split
 * vertices at one position disagree (measured on civ_aurelian_citizen's served rig: 74k coincident pairs, mean
 * cos 0.70 — 46° apart; 78 % differ by more than 8°) and a face drew as flat patches, one per tiny chart, with or
 * without its normal map. This re-derives each vertex's normal from the split normals AT ITS POSITION that lie
 * within the crease angle of its own — a smooth surface where the seams merely cut it, a hard edge kept where the
 * surface really folds (a collar, a plate). Vertex count, index, UVs and skin attributes stay exactly as they were,
 * so a SkinnedMesh keeps binding and every clip keeps playing. Pure (typed arrays in, a Float32Array out) — the
 * worker and the harness call the same function.
 */

export interface RigNormalWeldResult {
  /** one smoothed unit normal per ORIGINAL vertex (xyz) */
  normals: Float32Array;
  /** distinct positions */
  welded: number;
  /** vertices whose normal moved by more than 1° */
  changed: number;
  ms: number;
}

/** Smooth split normals across coincident positions within `creaseDeg` of each other. */
export function weldSplitNormals(positions: ArrayLike<number>, normals: ArrayLike<number>, vertexCount: number, creaseDeg = 60): RigNormalWeldResult {
  const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const t0 = now();
  const n = vertexCount;
  const P = positions, N = normals;
  let mnx = Infinity, mny = Infinity, mnz = Infinity, mxx = -Infinity, mxy = -Infinity, mxz = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = P[i * 3], y = P[i * 3 + 1], z = P[i * 3 + 2];
    if (x < mnx) mnx = x; if (x > mxx) mxx = x;
    if (y < mny) mny = y; if (y > mxy) mxy = y;
    if (z < mnz) mnz = z; if (z > mxz) mxz = z;
  }
  const ex = mxx - mnx, ey = mxy - mny, ez = mxz - mnz;
  const diag = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1;
  // weld by quantised position (a millionth of the diagonal — the skin bake's quantum), open addressing
  const q = diag * 1e-6;
  const qx = new Int32Array(n), qy = new Int32Array(n), qz = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    qx[i] = Math.round((P[i * 3] - mnx) / q);
    qy[i] = Math.round((P[i * 3 + 1] - mny) / q);
    qz[i] = Math.round((P[i * 3 + 2] - mnz) / q);
  }
  let cap = 1; while (cap < n * 2) cap <<= 1;
  const table = new Int32Array(cap);
  const weld = new Int32Array(n);
  let W = 0;
  for (let i = 0; i < n; i++) {
    let h = (Math.imul(qx[i], 73856093) ^ Math.imul(qy[i], 19349663) ^ Math.imul(qz[i], 83492791)) & (cap - 1);
    for (;;) {
      const r = table[h];
      if (r === 0) { table[h] = i + 1; weld[i] = W++; break; }
      const j = r - 1;
      if (qx[j] === qx[i] && qy[j] === qy[i] && qz[j] === qz[i]) { weld[i] = weld[j]; break; }
      h = (h + 1) & (cap - 1);
    }
  }
  // members of each weld (CSR)
  const start = new Int32Array(W + 1);
  for (let i = 0; i < n; i++) start[weld[i] + 1]++;
  for (let w = 0; w < W; w++) start[w + 1] += start[w];
  const fill = start.slice(0, W);
  const member = new Int32Array(n);
  for (let i = 0; i < n; i++) member[fill[weld[i]]++] = i;
  // unit input normals
  const U = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const x = N[i * 3], y = N[i * 3 + 1], z = N[i * 3 + 2];
    const l = Math.sqrt(x * x + y * y + z * z) || 1;
    U[i * 3] = x / l; U[i * 3 + 1] = y / l; U[i * 3 + 2] = z / l;
  }
  const cosCrease = Math.cos((creaseDeg * Math.PI) / 180);
  const cos1 = Math.cos(Math.PI / 180);
  const out = new Float32Array(n * 3);
  let changed = 0;
  for (let i = 0; i < n; i++) {
    const w = weld[i];
    const ax = U[i * 3], ay = U[i * 3 + 1], az = U[i * 3 + 2];
    let sx = 0, sy = 0, sz = 0;
    for (let k = start[w]; k < start[w + 1]; k++) {
      const j = member[k];
      const bx = U[j * 3], by = U[j * 3 + 1], bz = U[j * 3 + 2];
      if (ax * bx + ay * by + az * bz >= cosCrease) { sx += bx; sy += by; sz += bz; }
    }
    const l = Math.sqrt(sx * sx + sy * sy + sz * sz);
    if (l < 1e-9) { out[i * 3] = ax; out[i * 3 + 1] = ay; out[i * 3 + 2] = az; continue; }
    const nx = sx / l, ny = sy / l, nz = sz / l;
    out[i * 3] = nx; out[i * 3 + 1] = ny; out[i * 3 + 2] = nz;
    if (ax * nx + ay * ny + az * nz < cos1) changed++;
  }
  return { normals: out, welded: W, changed, ms: +(now() - t0).toFixed(1) };
}
